// Cave84 — Bottle counts per Vintage Chart region
//
// Maps the 20 vintage_charts regions to Firestore appellation/style data,
// returning a Map<regionId, bottleCount> for in_cellar bottles (all cellars).
//
// Matching logic uses appellation.region + appellation.name + wine.style.
// This is a best-effort mapping — edge cases may exist for unusual appellations.

import { useMemo } from 'react';
import { useGroupedInventory, DEFAULT_FILTERS, type GroupedInventoryRow } from '@/hooks/useInventory';

// ─── Region matchers ──────────────────────────────────────────────────────────

type RegionMatcher = (row: GroupedInventoryRow) => boolean;

function reg(row: GroupedInventoryRow): string {
  return (row.appellation?.region ?? '').toLowerCase();
}
function appName(row: GroupedInventoryRow): string {
  return (row.appellation?.name ?? '').toLowerCase();
}

const REGION_MATCHERS: Record<string, RegionMatcher> = {

  Burgundy_Red: (r) =>
    (reg(r).includes('burgundy') || reg(r).includes('bourgogne')) &&
    r.wine.style === 'red',

  Burgundy_White: (r) =>
    (reg(r).includes('burgundy') || reg(r).includes('bourgogne')) &&
    r.wine.style === 'white',

  Bordeaux_Left: (r) => {
    if (!reg(r).includes('bordeaux')) return false;
    const n = appName(r);
    return (
      n.includes('médoc') || n.includes('medoc') ||
      n.includes('pessac') || n.includes('margaux') ||
      n.includes('pauillac') || n.includes('saint-estèphe') ||
      n.includes('saint-estephe') || n.includes('saint-julien') ||
      n.includes('moulis') || n.includes('listrac') ||
      n.includes('haut-médoc') || n.includes('haut-medoc')
    );
  },

  Bordeaux_Right: (r) => {
    if (!reg(r).includes('bordeaux')) return false;
    const n = appName(r);
    return (
      n.includes('saint-émilion') || n.includes('saint emilion') ||
      n.includes('pomerol') || n.includes('fronsac') ||
      n.includes('canon-fronsac') || n.includes('lalande')
    );
  },

  Sauternes: (r) => {
    const n = appName(r);
    return n.includes('sauternes') || n.includes('barsac');
  },

  Rhone_North: (r) => {
    const n = appName(r);
    return (
      n.includes('côte-rôtie') || n.includes('cote-rotie') || n.includes('cote rotie') ||
      n.includes('hermitage') ||
      n.includes('cornas') ||
      n.includes('saint-joseph') || n.includes('saint joseph') ||
      n.includes('crozes-hermitage') || n.includes('crozes hermitage') ||
      n.includes('condrieu')
    );
  },

  Rhone_South: (r) => {
    const n = appName(r);
    return (
      n.includes('châteauneuf') || n.includes('chateauneuf') ||
      n.includes('gigondas') ||
      n.includes('vacqueyras') ||
      n.includes('lirac') ||
      n.includes('tavel')
    );
  },

  Champagne: (r) =>
    reg(r).includes('champagne') || appName(r).includes('champagne'),

  Barolo: (r) =>
    appName(r).includes('barolo') && !appName(r).includes('barbaresco'),

  Brunello: (r) =>
    appName(r).includes('brunello') || appName(r).includes('rosso di montalcino'),

  Bolgheri: (r) =>
    appName(r).includes('bolgheri') || appName(r).includes('sassicaia'),

  Loire: (r) =>
    reg(r).includes('loire') ||
    appName(r).includes('sancerre') ||
    appName(r).includes('pouilly-fumé') || appName(r).includes('pouilly fume') ||
    appName(r).includes('vouvray') ||
    appName(r).includes('chinon') ||
    appName(r).includes('bourgueil'),

  Alsace: (r) =>
    reg(r).includes('alsace') || appName(r).includes('alsace'),

  Mosel_Rheingau: (r) =>
    (reg(r).includes('mosel') || appName(r).includes('mosel') ||
    reg(r).includes('rheingau') || appName(r).includes('rheingau')) &&
    !reg(r).includes('nahe') && !appName(r).includes('nahe'),

  Rioja: (r) =>
    reg(r).includes('rioja') || appName(r).includes('rioja'),

  Ribera_del_Duero: (r) =>
    appName(r).includes('ribera del duero') || reg(r).includes('ribera del duero'),

  Priorat: (r) =>
    reg(r).includes('priorat') || appName(r).includes('priorat'),

  Tokaji: (r) =>
    reg(r).includes('tokaj') || appName(r).includes('tokaj'),

  Nahe: (r) =>
    reg(r).includes('nahe') || appName(r).includes('nahe'),

  Amarone: (r) =>
    appName(r).includes('amarone'),
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns a Map<vintageChartRegionId, bottleCount> for all in_cellar bottles
 * (across ALL cellars — not filtered to home only).
 */
export function useBottleCountByVintageChartRegion(): {
  counts: Map<string, number>;
  isLoading: boolean;
} {
  const { grouped, isLoading } = useGroupedInventory({
    ...DEFAULT_FILTERS,
    availableAtHome: false,
  });

  const counts = useMemo(() => {
    const result = new Map<string, number>();
    for (const [regionId, matcher] of Object.entries(REGION_MATCHERS)) {
      const total = grouped
        .filter(matcher)
        .reduce((sum, r) => sum + r.qty, 0);
      if (total > 0) result.set(regionId, total);
    }
    return result;
  }, [grouped]);

  return { counts, isLoading };
}
